import { useCallback, useEffect, useRef, useState } from "react";
import "./HowTo.css";
import { keyframes } from "@emotion/react";
import { useHistory } from "react-router";
import {
  Paper,
  Stack,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Flipper, Flipped } from "react-flip-toolkit";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { Cue, CueTypes, Question } from "../../shared/interfaces/GameLogicTypes";
import { getFontSizeByAnswer } from "../../shared/helper/functions";
import QuestionHeader from "../../components/AlternateQuestionHeader/AlternateQuestionHeader";
import CardDeck from "../../components/CardDeck/CardDeck";
import FrontPageLogo from "../../components/Logo/Logo";

// ─── Colors (same as SequenceBar) ────────────────────────

const BAR_BG = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const BAR_FG = ["#333333", "#ffffff", "#ffffff", "#ffffff", "#ffffff"];

// ─── Demo data defaults (overridden by howto.md) ───────────────────────────

const DEFAULT_DEMO = {
  tsarName: "Maya",
  question: {
    forTsar: "What do you think shapes a person the most?",
    aboutTsar: "What does <h>{{tsar}}</h> think shapes a person the most?",
  },
  cues: [
    "The friends they keep",
    "Their biggest failure",
    "Where they grew up",
    "The music they listen to",
    "A moment of kindness",
  ],
  czarSequence: [2, 5, 1, 3, 4],
  playerSequence: [1, 2, 5, 3, 4],
  friends: [
    { name: "Alex",   sequence: [2, 1, 5, 4, 3], reason: "\"She failed that exam and never shut up about it - definitely her #1.\"" },
    { name: "Jordan", sequence: [1, 5, 2, 3, 4], reason: "\"Maya literally has 300 friends. Obviously it's the people around her.\"" },
    { name: "Sam",    sequence: [5, 3, 4, 1, 2], reason: "\"Idk, she's always super nice to people - kindness has to be first.\"" },
    { name: "Taylor", sequence: [2, 5, 1, 3, 4], reason: "\"She told me once that getting knocked down changed her whole worldview.\"" },
  ],
};

type HowToConfig = typeof DEFAULT_DEMO;

const EXAMPLE_DECKS = [
  { name: "Deep Questions\nAbout Life", id: "demo-1", featured: true, language: { id: "en", name: "English" }, creatorName: "rank5team", emoji: "🤔" },
  { name: "Would You Rather", id: "demo-2", featured: false, language: { id: "en", name: "English" }, creatorName: "partypack", emoji: "🎲" },
  { name: "Pop Culture\nShowdown", id: "demo-3", featured: true, language: { id: "en", name: "English" }, creatorName: "culturejunkie", emoji: "🎬" },
  { name: "Controversial\nOpinions", id: "demo-4", featured: false, language: { id: "en", name: "English" }, creatorName: "hotakes", emoji: "🔥" },
];

// ─── Score calculation (mirrors game.ts calculatePlayerResults) ───

function calculateScore(sequence: number[], czarSequence: number[]): number {
  const temp = [...sequence];
  let points = 2000;

  while (temp.join(",") !== czarSequence.join(",")) {
    // Find all possible swaps with scored distances
    const swaps: { cost: number; swap: [number, number] }[] = [];
    for (let j = 0; j < 5; j++) {
      if (temp[j] !== czarSequence[j]) {
        const targetIdx = czarSequence.indexOf(temp[j]);
        const dist = Math.abs(j - targetIdx);
        swaps.push({ cost: dist * dist * 100, swap: [j, targetIdx] });
      }
    }

    // Deduplicate (e.g. [1,2] and [2,1] are the same swap)
    const unique = swaps.filter((s, i) => {
      const key = [...s.swap].sort().join(",");
      return (
        swaps.findIndex(
          (s2) => [...s2.swap].sort().join(",") === key
        ) === i
      );
    });

    const minCost = Math.min(...unique.map((s) => s.cost));
    const cheapest = unique.filter((s) => s.cost === minCost);

    let best = cheapest[0];
    if (cheapest.length > 1) {
      // Tie-break: pick the swap that leads to the highest remaining score
      best = cheapest.reduce((acc, s) => {
        const seqAcc = [...temp];
        [seqAcc[acc.swap[0]], seqAcc[acc.swap[1]]] = [seqAcc[acc.swap[1]], seqAcc[acc.swap[0]]];
        const seqS = [...temp];
        [seqS[s.swap[0]], seqS[s.swap[1]]] = [seqS[s.swap[1]], seqS[s.swap[0]]];
        return calculateScore(seqS, czarSequence) > calculateScore(seqAcc, czarSequence)
          ? s
          : acc;
      });
    }

    [temp[best.swap[0]], temp[best.swap[1]]] = [temp[best.swap[1]], temp[best.swap[0]]];
    points -= best.cost;
  }

  return points;
}

// ─── Helpers ─────────────────────────────────────────────

function randomSwap(seq: number[]): number[] {
  const next = [...seq];
  const i = Math.floor(Math.random() * 5);
  let j = Math.floor(Math.random() * 4);
  if (j >= i) j++;
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// ─── Demo Ranking Panel ──────────────────────────────────

interface DemoRankingPanelProps {
  id: string;
  sequence: number[];
  cues: Cue[];
  label?: string;
  score?: number;
  locked?: boolean;
  dimmed?: boolean;
  showLockButton?: boolean;
  compact?: boolean;
  isMobile?: boolean;
  /** Skip Flipper/Flipped wrappers — use for static panels inside transformed containers */
  static?: boolean;
  /** Animate score counting from 0 to final value */
  animateScore?: boolean;
}

function DemoRankingPanel(props: DemoRankingPanelProps) {
  const {
    id,
    sequence,
    cues,
    label,
    score,
    locked,
    dimmed,
    showLockButton,
    compact,
    isMobile,
  } = props;
  const isStatic = props.static;
  const animateScore = props.animateScore;

  const [displayScore, setDisplayScore] = useState(0);

  // Animate score counting when animateScore is triggered
  useEffect(() => {
    if (!animateScore || score === undefined) return;

    let current = 0;
    const increment = Math.ceil(score / 20); // Divide into 20 steps
    const interval = setInterval(() => {
      current += increment;
      if (current >= score) {
        setDisplayScore(score);
        clearInterval(interval);
      } else {
        setDisplayScore(current);
      }
    }, 30);

    return () => clearInterval(interval);
  }, [animateScore, score]);
  return (
    <Box
      sx={{
        position: "relative",
        width: compact ? (isMobile ? "70vw" : 300) : "100%",
        flexShrink: 0,
      }}
    >
      {label && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {label}
          </Typography>
          {score !== undefined && (
            <Typography variant="subtitle2" color="primary" fontWeight={700}>
              {animateScore ? displayScore : score} pts
            </Typography>
          )}
        </Stack>
      )}
      <Paper
        sx={{
          width: "100%",
          px: compact ? 1 : 1.5,
          pt: compact ? 1 : 1.5,
          pb: compact ? 1 : 1.5,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {(() => {
          const renderBar = (item: number, index: number) => (
            <Box
              key={item}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: compact ? 0.5 : 1,
              }}
            >
              <Typography
                sx={{
                  minWidth: compact ? 18 : 22,
                  textAlign: "right",
                  color: "text.secondary",
                  fontSize: compact ? 12 : 14,
                  flexShrink: 0,
                  userSelect: "none",
                }}
              >
                {index + 1}.
              </Typography>
              <Box
                style={{
                  flex: 1,
                  backgroundColor: BAR_BG[item - 1],
                  color: BAR_FG[item - 1],
                  borderRadius: 6,
                  padding: compact
                    ? "7px 10px"
                    : isMobile
                    ? "13px 14px"
                    : "10px 14px",
                  minHeight: compact ? 36 : isMobile ? 52 : 48,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: compact
                      ? 11
                      : getFontSizeByAnswer(cues[item - 1]?.content ?? ""),
                    fontWeight: 600,
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    color: "inherit",
                  }}
                >
                  {cues[item - 1]?.content}
                </Typography>
              </Box>
              <DragIndicatorIcon
                sx={{
                  color: "text.disabled",
                  flexShrink: 0,
                  fontSize: compact ? 16 : 22,
                }}
              />
            </Box>
          );

          if (isStatic) {
            return (
              <Stack spacing={compact ? 0.75 : 1} sx={{ mb: showLockButton ? 1.5 : 0 }}>
                {sequence.map(renderBar)}
              </Stack>
            );
          }

          return (
            <Flipper
              flipKey={sequence.join("")}
              spring={compact ? { stiffness: 280, damping: 22 } : { stiffness: 180, damping: 20 }}
            >
              <Stack spacing={compact ? 0.75 : 1} sx={{ mb: showLockButton ? 1.5 : 0 }}>
                {sequence.map((item, index) => (
                  <Flipped key={item} flipId={`${id}-${item}`} translate>
                    {renderBar(item, index)}
                  </Flipped>
                ))}
              </Stack>
            </Flipper>
          );
        })()}

        {showLockButton && (
          <Button
            variant={locked ? "outlined" : "contained"}
            fullWidth
            disabled={locked}
            sx={{
              borderRadius: 2,
              fontWeight: "bold",
              fontSize: compact ? "0.85rem" : "1rem",
              py: compact ? 1 : 1.5,
              transition: "all 0.3s ease",
            }}
          >
            {locked ? "Locked In" : "Lock In"}
          </Button>
        )}

        {/* Gray overlay for "hidden" state */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(30, 30, 30, 0.6)",
            borderRadius: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: dimmed ? 1 : 0,
            pointerEvents: dimmed ? "auto" : "none",
            transition: "opacity 0.5s ease",
            zIndex: 10,
            backdropFilter: "blur(3px)",
          }}
        >
          <VisibilityOffIcon
            sx={{ fontSize: 56, color: "rgba(255,255,255,0.75)" }}
          />
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Animations ──────────────────────────────────────

const lockInPulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.92);
  }
  100% {
    transform: scale(1);
  }
`;

// ─── Main Component ──────────────────────────────────────

export default function HowTo() {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ─── Config state (overridable via /howto.json or /howto.md) ────────────
  const [cfg, setCfg] = useState<HowToConfig>(DEFAULT_DEMO);
  const [cueObjs, setCueObjs] = useState<Cue[]>(
    DEFAULT_DEMO.cues.map((c) => ({ type: CueTypes.text, content: c }))
  );
  const [czarSequence, setCzarSequence] = useState<number[]>([...DEFAULT_DEMO.czarSequence]);
  const [playerSeq, setPlayerSeq] = useState<number[]>([...DEFAULT_DEMO.playerSequence]);
  const [friends, setFriends] = useState(
    DEFAULT_DEMO.friends.map((f) => ({ ...f, score: calculateScore(f.sequence, DEFAULT_DEMO.czarSequence) }))
  );
  const BEST_FRIEND = friends.reduce((a, b) => (a.score > b.score ? a : b));
  const PLAYER_SCORE = calculateScore(playerSeq, cfg.czarSequence);

  // Czar animation state
  const [czarDimmed, setCzarDimmed] = useState(false);
  const [shuffling, setShuffling] = useState(false);

  // Player prediction — interactive drag
  const [playerDragging, setPlayerDragging] = useState(false);
  const [playerLocked, setPlayerLocked] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onPlayerDragStart = useCallback(() => {
    setPlayerDragging(true);
    setPlayerLocked(false);
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const onPlayerDragEnd = useCallback((result: DropResult) => {
    // Apply the reorder if valid
    if (result.destination) {
      setPlayerSeq((prev) => {
        const next = [...prev];
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination!.index, 0, moved);
        return next;
      });
    }
    // Snap back to original after a moment, then clear the message
    resetTimerRef.current = setTimeout(() => {
      setPlayerSeq([...cfg.playerSequence]);
      setPlayerLocked(true);
      setPlayerDragging(false);
    }, 1200);
  }, [cfg]);

  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const czarPanelRef = useRef<HTMLDivElement>(null);
  const hiddenSectionRef = useRef<HTMLDivElement>(null);
  const lockButtonRef = useRef<HTMLButtonElement>(null);
  const explanationRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const revealSectionRef = useRef<HTMLDivElement>(null);
  const shuffleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Frontier checkpoint states — one-way triggers (stay true once crossed)
  const [buttonCrossedFrontier, setButtonCrossedFrontier] = useState(false);
  const [explanationCrossedFrontier, setExplanationCrossedFrontier] = useState(false);
  const [carouselCrossedFrontier, setCarouselCrossedFrontier] = useState(false);
  const [revealSectionCrossedFrontier, setRevealSectionCrossedFrontier] = useState(false);

  // Start continuous shuffle when czar panel enters viewport
  useEffect(() => {
    const el = czarPanelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !shuffling) {
          setShuffling(true);
        }
      },
      { threshold: 0.3, root: scrollContainerRef.current }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shuffling]);

  // Run the shuffle with random delays while shuffling and not dimmed
  useEffect(() => {
    if (!shuffling || czarDimmed) {
      if (shuffleRef.current) {
        clearTimeout(shuffleRef.current);
        shuffleRef.current = null;
      }
      return;
    }

    const scheduleNext = () => {
      // Base delay 1800ms, occasionally insert an extra 3000ms pause (~20% chance)
      const extraPause = Math.random() < 0.2 ? 3000 : 0;
      const delay = 1800 + extraPause;
      shuffleRef.current = setTimeout(() => {
        setCzarSequence((prev) => randomSwap(prev));
        scheduleNext();
      }, delay);
    };

    scheduleNext();

    return () => {
      if (shuffleRef.current) {
        clearTimeout(shuffleRef.current);
        shuffleRef.current = null;
      }
    };
  }, [shuffling, czarDimmed]);

  // Dim when "but you don't see that" crosses the vertical center of the viewport.
  // Stays dimmed even after scrolling past; only undims if you scroll back ABOVE it.
  useEffect(() => {
    const el = hiddenSectionRef.current;
    if (!el) return;

    const root = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCzarDimmed(true);
        } else {
          const rootH = root ? root.clientHeight : window.innerHeight;
          const rect = entry.boundingClientRect;
          const rootRect = root ? root.getBoundingClientRect() : { top: 0 };
          if (rect.top - rootRect.top > rootH / 2) {
            setCzarDimmed(false);
          }
        }
      },
      { threshold: 0, rootMargin: "-50% 0px -50% 0px", root }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // When fully dimmed, snap czar sequence back to her actual ranking
  useEffect(() => {
    if (czarDimmed) {
      setCzarSequence([...cfg.czarSequence]);
    }
  }, [czarDimmed, cfg]);

  // Track when Lock In button crosses the frontier (50% viewport center)
  useEffect(() => {
    const el = lockButtonRef.current;
    if (!el) return;

    const root = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setButtonCrossedFrontier(true);
        } else {
          const rootH = root ? root.clientHeight : window.innerHeight;
          const rect = entry.boundingClientRect;
          const rootRect = root ? root.getBoundingClientRect() : { top: 0 };
          if (rect.top - rootRect.top > rootH / 2) {
            setButtonCrossedFrontier(false);
          }
        }
      },
      { threshold: 0, rootMargin: "-50% 0px -50% 0px", root }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Track when explanation (cursive) text crosses the frontier (50% viewport center)
  useEffect(() => {
    const el = explanationRef.current;
    if (!el) return;

    const root = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setExplanationCrossedFrontier(true);
        } else {
          const rootH = root ? root.clientHeight : window.innerHeight;
          const rect = entry.boundingClientRect;
          const rootRect = root ? root.getBoundingClientRect() : { top: 0 };
          if (rect.top - rootRect.top > rootH / 2) {
            setExplanationCrossedFrontier(false);
          }
        }
      },
      { threshold: 0, rootMargin: "-50% 0px -50% 0px", root }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Track when carousel crosses the frontier (one-way trigger for friend scores)
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCarouselCrossedFrontier(true);
        }
      },
      { threshold: 0, rootMargin: "-50% 0px -50% 0px", root: scrollContainerRef.current }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Track when "Then the truth comes out" section crosses frontier
  useEffect(() => {
    const el = revealSectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealSectionCrossedFrontier(true);
        }
      },
      { threshold: 0, rootMargin: "-30% 0px -70% 0px", root: scrollContainerRef.current }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Clean up player reset timer on unmount
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const panelMaxW = isMobile ? "100%" : 520;
  const textMaxW = isMobile ? "100%" : 620;

  // ─── Carousel smooth scroller ───────────────────────────────────────────
  const trackRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number>(0);
  const cycleWRef = useRef<number>(0);
  const offsetRef = useRef<number>(0);
  const speedRef = useRef<number>(30);      // current px/sec
  const targetSpeedRef = useRef<number>(30); // target px/sec

  const setTargetSpeed = useCallback((pxPerSec: number) => {
    targetSpeedRef.current = pxPerSec;
  }, []);

  useEffect(() => {
    // Load JSON config only
    (async () => {
      try {
        const resJson = await fetch("/howto.json", { cache: "no-cache" });
        if (!resJson.ok) return;
        const parsed = (await resJson.json()) as HowToConfig;
        // Basic validation
        if (!Array.isArray(parsed.cues) || parsed.cues.length !== 5) return;
        const seqOk = (a: number[]) => Array.isArray(a) && a.length === 5;
        if (!seqOk(parsed.czarSequence) || !seqOk(parsed.playerSequence)) return;

        const cfgLoaded = parsed as HowToConfig;
        const czarSeq = cfgLoaded.czarSequence;
        setCfg(cfgLoaded);
        setCzarSequence([...czarSeq]);
        setPlayerSeq([...cfgLoaded.playerSequence]);
        setCueObjs(cfgLoaded.cues.map((c) => ({ type: CueTypes.text, content: c })));
        const fr = (cfgLoaded.friends ?? []).map((f) => ({ ...f, score: calculateScore(f.sequence, czarSeq) }));
        setFriends(fr);
      } catch (e) {
        console.warn("howto config not loaded or invalid", e);
      }
    })();

    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      // Half the width because items are duplicated for seamless loop
      cycleWRef.current = el.scrollWidth / 2;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    const tick = (ts: number) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      // Ease current speed toward target
      const s = speedRef.current + (targetSpeedRef.current - speedRef.current) * 0.12;
      speedRef.current = s;

      // Advance offset
      offsetRef.current += s * dt;
      const cycle = cycleWRef.current || 1;
      if (offsetRef.current > cycle) offsetRef.current -= cycle;

      // Apply transform
      el.style.transform = `translateX(${-offsetRef.current}px)`;

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="column rank5background howTo" ref={scrollContainerRef}>
      {/* ═══ LOGO + HERO ════════════════════════════════ */}
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <FrontPageLogo />
      </Box>
      <Box
        sx={{
          textAlign: "center",
          pt: 2,
          pb: 2,
          maxWidth: textMaxW,
          mx: "auto",
          width: "100%",
        }}
      >
        <Typography
          component="h1"
          variant="h3"
          fontWeight={800}
          sx={{ mb: 1.5, fontSize: { xs: 26, md: 36 } }}
        >
          A party game about knowing each other
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: { xs: 15, md: 17 }, lineHeight: 1.7, mb: 3 }}
        >
          rank5 is a cooperative party game where you find out how well you
          actually know your friends. You talk, you argue, you learn stuff
          about each other you never expected. Works on any device - phones,
          laptops, tablets - all in the same lobby.
        </Typography>
      </Box>

      {/* ═══ DECK SELECTION ═════════════════════════════ */}
      <Box sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Pick your card decks
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Hundreds of decks to choose from - deep philosophical questions,
          spicy hot takes, niche inside jokes. There's a whole community of
          creators writing new ones all the time.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(4, 1fr)",
            },
            gap: 1.5,
          }}
        >
          {EXAMPLE_DECKS.map((deck) => (
            <CardDeck
              key={deck.id}
              id={deck.id}
              title={deck.name}
              language={deck.language}
              emoji={deck.emoji}
              hideLanguage
            />
          ))}
        </Box>
      </Box>

      {/* ═══ THE CZAR ═══════════════════════════════════ */}
      <Box sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Every turn, someone is in the spotlight
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every turn, one person is in the spotlight — that's your friend <b>Maya</b>. They get a question and have to
          rank five cards honestly.
        </Typography>
      </Box>

      {/* Question header */}
      <Box sx={{ maxWidth: panelMaxW, mx: "auto", width: "100%", mb: 2 }}>
        <QuestionHeader
          question={cfg.question as Question}
          tsarName={cfg.tsarName}
          isTsar={false}
          isMobile={isMobile}
        />
      </Box>

      {/* Czar's animated ranking panel — shuffles continuously */}
      <Box
        ref={czarPanelRef}
        sx={{ maxWidth: panelMaxW, mx: "auto", width: "100%", mb: 0 }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mb: 0.5, color: "text.secondary" }}
        >
          {cfg.tsarName}'s ranking:
        </Typography>
        <DemoRankingPanel
          id="czar"
          sequence={czarSequence}
          cues={cueObjs}
          dimmed={czarDimmed}
          isMobile={isMobile}
        />
      </Box>

      {/* ═══ BUT YOU DON'T SEE THAT ════════════════════ */}
      <Box
        ref={hiddenSectionRef}
        sx={{
          maxWidth: textMaxW,
          mx: "auto",
          width: "100%",
          py: { xs: 4, md: 5 },
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ mb: 1.5, fontSize: { xs: 22, md: 28 } }}
        >
          But you don't see that.
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7, maxWidth: 480, mx: "auto" }}
        >
          Maya's ranking is hidden from everyone else. You have to
          predict how they ordered the cards. What do you think matters
          most to them?
        </Typography>
      </Box>

      {/* ═══ YOUR PREDICTION ═══════════════════════════ */}
      <Box sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Your turn to predict
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag the cards into the order you think {cfg.tsarName} chose.
          Go with your gut - what do you know about her?
        </Typography>
      </Box>

      <Box sx={{ maxWidth: panelMaxW, mx: "auto", width: "100%", mb: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Your prediction
          </Typography>
        </Stack>
        <Paper
          sx={{
            width: "100%",
            px: 1.5,
            pt: 1.5,
            pb: 1.5,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <DragDropContext onDragStart={onPlayerDragStart} onDragEnd={onPlayerDragEnd}>
            <Flipper
              flipKey={playerSeq.join("")}
              spring={{ stiffness: 220, damping: 22 }}
            >
              <Droppable droppableId="howToPlayer" direction="vertical">
                {(provided) => (
                  <Stack
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    spacing={1}
                    sx={{ mb: 1.5 }}
                  >
                    {playerSeq.map((item, index) => (
                      <Draggable
                        key={`player-${item}`}
                        draggableId={`player-${item}`}
                        index={index}
                      >
                        {(dragProvided, snapshot) => (
                          <Flipped
                            flipId={`player-${item}`}
                            translate
                            shouldFlip={() => !snapshot.isDragging}
                          >
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                opacity: snapshot.isDragging ? 0.85 : 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  minWidth: 22,
                                  textAlign: "right",
                                  color: "text.secondary",
                                  fontSize: 14,
                                  flexShrink: 0,
                                  userSelect: "none",
                                }}
                              >
                                {index + 1}.
                              </Typography>
                              <Box
                                style={{
                                  flex: 1,
                                  backgroundColor: BAR_BG[item - 1],
                                  color: BAR_FG[item - 1],
                                  borderRadius: 6,
                                  padding: isMobile ? "13px 14px" : "10px 14px",
                                  minHeight: isMobile ? 52 : 48,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: getFontSizeByAnswer(
                                      cueObjs[item - 1]?.content ?? ""
                                    ),
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                    wordBreak: "break-word",
                                    color: "inherit",
                                  }}
                                >
                                  {cueObjs[item - 1]?.content}
                                </Typography>
                              </Box>
                              <DragIndicatorIcon
                                sx={{ color: "text.disabled", flexShrink: 0, fontSize: 22 }}
                              />
                            </Box>
                          </Flipped>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </Flipper>
          </DragDropContext>

          <Button
            ref={lockButtonRef}
            variant="contained"
            fullWidth
            disabled={buttonCrossedFrontier && explanationCrossedFrontier}
            sx={{
              borderRadius: 2,
              fontWeight: "bold",
              fontSize: "1rem",
              py: 1.5,
              transition: "all 0.3s ease",
              opacity: buttonCrossedFrontier && explanationCrossedFrontier ? 0.6 : 1,
              cursor: buttonCrossedFrontier && explanationCrossedFrontier ? "not-allowed" : "pointer",
              animation: buttonCrossedFrontier ? `${lockInPulse} 0.6s ease-out` : "none",
            }}
            title={
              buttonCrossedFrontier && explanationCrossedFrontier
                ? "Scroll past the explanation to unlock"
                : !buttonCrossedFrontier
                ? "Scroll down to lock in"
                : ""
            }
          >
            {buttonCrossedFrontier ? "Locked In" : "Lock In"}
          </Button>
        </Paper>
      </Box>

      <Box ref={explanationRef} sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 5, mt: 1 }}>
        <Paper
          sx={{
            p: 2,
            backgroundColor: playerDragging
              ? "rgba(236, 119, 119, 0.15)"
              : "rgba(255,255,255,0.65)",
            transition: "background-color 0.3s ease",
          }}
        >
          <Typography
            variant="body2"
            color={playerDragging ? "primary" : "text.secondary"}
            sx={{
              lineHeight: 1.7,
              fontStyle: playerDragging ? "normal" : "italic",
              fontWeight: playerDragging ? 700 : 400,
              textTransform: playerDragging ? "uppercase" : "none",
              transition: "all 0.2s ease",
              fontSize: playerDragging ? 15 : undefined,
            }}
          >
            {playerDragging
              ? `You don't know ${cfg.tsarName}.`
              : `You put "The friends they keep" first - ${cfg.tsarName} is the most social person you know. "Their biggest failure" felt like her number two. Makes sense, she talks about that stuff all the time. But did you read her right?`}
          </Typography>
        </Paper>
      </Box>

      {/* ═══ FRIENDS' SUBMISSIONS ══════════════════════ */}
      <Box sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Everyone else does the same
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          All your friends lock in their predictions too. Everyone has a
          different read on {cfg.tsarName}.
        </Typography>
      </Box>

      {/* Auto-scrolling carousel with edge fade */}
      <Box
        ref={carouselRef}
        className="howToCarouselMask"
        sx={{
          width: "100vw",
          ml: { xs: "-5%", md: "-15%" },
          overflow: "hidden",
          pb: 2,
          mb: 5,
        }}
        onMouseEnter={() => setTargetSpeed(0)}
        onMouseLeave={() => setTargetSpeed(30)}
      >
        <div className="howToCarouselTrack" ref={trackRef}>
          {[0, 1].flatMap((copy) =>
            [
              { name: "You", sequence: playerSeq, score: PLAYER_SCORE, reason: "\"She's so social - friends have to be her #1.\"" },
              ...friends,
            ].map((person) => (
              <Box key={`${copy}-${person.name}`} sx={{ flexShrink: 0 }}>
                <DemoRankingPanel
                  id={`carousel-${copy}-${person.name}`}
                  sequence={person.sequence}
                  cues={cueObjs}
                  label={person.name}
                  score={carouselCrossedFrontier ? person.score : undefined}
                  compact
                  static
                  isMobile={isMobile}
                  animateScore={carouselCrossedFrontier}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 0.75,
                    display: "block",
                    fontStyle: "italic",
                    lineHeight: 1.4,
                    px: 0.5,
                    width: isMobile ? "70vw" : 300,
                  }}
                >
                  {person.reason}
                </Typography>
              </Box>
            ))
          )}
        </div>
      </Box>

      {/* ═══ THE REVEAL ════════════════════════════════ */}
      <Box ref={revealSectionRef} sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Then the truth comes out
        </Typography>
      </Box>

      <Box sx={{ maxWidth: panelMaxW, mx: "auto", width: "100%", mb: 2 }}>
        <DemoRankingPanel
          id="reveal"
          sequence={czarSequence}
          cues={cueObjs}
          label={`${cfg.tsarName}'s actual ranking`}
          isMobile={isMobile}
        />
      </Box>

      <Box sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 5 }}>
        <Paper sx={{ p: 2, backgroundColor: "rgba(255,255,255,0.65)" }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            {cfg.tsarName} put <b>"Their biggest failure"</b> at the very top.
            She believes what breaks you is what builds you. And{" "}
            <b>"A moment of kindness"</b> as her number two? You didn't see
            that coming. You scored <b>{PLAYER_SCORE}</b> out of 2000 - not
            bad. But {BEST_FRIEND.name} read her like a book:{" "}
            <b>{BEST_FRIEND.score} points</b>.
          </Typography>
        </Paper>
      </Box>

      {/* ═══ CLOSING ═══════════════════════════════════ */}
      <Box
        sx={{
          textAlign: "center",
          pt: 2,
          pb: 4,
          maxWidth: textMaxW,
          mx: "auto",
          width: "100%",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ mb: 2, fontSize: { xs: 20, md: 26 }, lineHeight: 1.5 }}
        >
          That's basically it.
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, fontSize: { xs: 15, md: 17 } }}
        >
          Every turn sparks a conversation, every round someone gets exposed.
          Grab your friends and find out who actually knows who.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => history.push("/play")}
          sx={{
            borderRadius: 3,
            px: 5,
            py: 1.5,
            fontWeight: 700,
            fontSize: "1.1rem",
          }}
        >
          Play Now
        </Button>
      </Box>

      <Box sx={{ pb: 6 }} />
    </div>
  );
}
